/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useDataConnector } from '@forge/data-connector';
import React, { useState, useRef } from 'react';
import IComponentProps from '../useConnectorAndDynamicProps/IComponentProps';
import { v4 as uuid4 } from 'uuid';
import { getBearerTokenFromLS } from '../Utils/DataConnectorUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';

export default function useConnectorAndDynamicProps(
  props: IComponentProps,
  invokeOnInit: boolean = true
) {
  const startTime = props.requestContext?.context?.startTime;
  const endTime = props.requestContext?.context?.endTime;

  const [newUuuid, setUuuid] = useState(uuid4());
  const previousStartTimeInputValue = useRef<string | undefined>(startTime);
  const previousEndTimeInputValue = useRef<string | undefined>(endTime);

  // On Date change new X-SVCorId generate
  if (previousStartTimeInputValue.current !== startTime) {
    setUuuid(uuid4());
    previousStartTimeInputValue.current = startTime;
  }
  if (previousEndTimeInputValue.current !== endTime) {
    setUuuid(uuid4());
    previousEndTimeInputValue.current = endTime;
  }

  const requestHeader = props.requestContext.headers
    ? {
        'Content-Type': 'application/json',
        authorization: `Bearer ${getBearerTokenFromLS()}`,
        'X-SVCorId': newUuuid,
        ...props.requestContext.headers
      }
    : {
        'Content-Type': 'application/json',
        'X-SVCorId': newUuuid,
        authorization: `Bearer ${getBearerTokenFromLS()}`
      };

  const route = {
    rootnodes: props.requestContext.rootNodeId ?? '',
    nodes: props.requestContext.context?.nodeId ?? '',
    sites: props.requestContext.context?.siteId ?? '',
    searchKeyword: props.requestContext.context?.searchKeyword ?? '',
    providerId: props.requestContext.context?.providerId ?? '',
    // append routes set by connectorConfig prop
    ...props.connectorConfig.routes
  };
  const dataConnector = useDataConnector(props.connectorConfig.name, {
    headers: requestHeader,
    params: {
      ...props.connectorConfig.options
    },
    route: route,
    data: props.requestContext
  });

  React.useEffect(() => {
    if (invokeOnInit && dataConnector?.actions?.getAll) {
      dataConnector.actions.getAll().then((result: IAny) => {
        // Initializing an empty arrow function
      });
    }
  }, [!!dataConnector]);

  return [dataConnector];
}
